import axios, { GenericAbortSignal } from 'axios';
import { businessPlanAxios } from 'services/controllers/businessPlan';

import * as t from './types';

const PROGRAMMATIC_ABORT_REASON = 'programmatic-abort';

let controller = new AbortController();

const getAbortReason = (signal?: GenericAbortSignal) => {
  if (!signal || !('reason' in signal)) {
    return undefined;
  }

  return (signal as GenericAbortSignal & { reason?: unknown }).reason;
};

export const BusinessPlanAIApi = {
  generateFieldCustomizable: async (
    { blocks = [], id, useCustomModel }: t.IBusinessPlanAIParams,
    action?: t.IAIActionTypes,
    signal?: GenericAbortSignal,
  ) => {
    const aiAction = action ?? t.AI_ACTION_TYPES.GENERATE_FIELD;
    const { data } = await businessPlanAxios.post<t.IBusinessPlanAIResponse>(
      `${id}/ai/${aiAction}`,
      { blocks, useCustomModel },
      { signal: signal ?? controller.signal, timeout: 60000 },
    );
    return data;
  },

  // Field
  generateField: async ({ id, requestParams = [], useCustomModel }: t.IBusinessPlanAIParams /*, signal?: GenericAbortSignal*/) => {
    const data = await BusinessPlanAIApi.generateFieldCustomizable({ blocks: requestParams, id, useCustomModel });
    return data;
  },

  improveField: async ({ id, requestParams = [], useCustomModel }: t.IBusinessPlanAIParams) => {
    const data = await BusinessPlanAIApi.generateFieldCustomizable(
      { blocks: requestParams, id, useCustomModel },
      t.AI_ACTION_TYPES.REGENERATE_FIELD,
    );
    return data;
  },

  shorter: async ({ id, names: blocks, text, useCustomModel }: t.IBusinessPlanAIParams) => {
    const { data } = await businessPlanAxios.post<t.IBusinessPlanAIResponse>(`${id}/ai/grammar/shorter`, {
      blocks,
      text,
      useCustomModel
    });
    return data;
  },

  longer: async ({ id, names: blocks, text, useCustomModel }: t.IBusinessPlanAIParams) => {
    const { data } = await businessPlanAxios.post<t.IBusinessPlanAIResponse>(`${id}/ai/grammar/longer`, {
      blocks,
      text,
      useCustomModel
    });
    return data;
  },

  fixGrammar: async ({ id, names: blocks, text, useCustomModel }: t.IBusinessPlanAIParams) => {
    const { data } = await businessPlanAxios.post<t.IBusinessPlanAIResponse>(`${id}/ai/grammar/fix`, {
      blocks,
      text: typeof text === 'object' ? `${JSON.stringify(text)}` : text,
      useCustomModel
    });
    return data;
  },

  // Chapter
  generateChapter: async ({ id, names: blocks, useCustomModel }: t.IBusinessPlanAIParams, signal?: GenericAbortSignal) => {
    const { data } = await businessPlanAxios.post<t.IBusinessPlanAIResponse>(
      `${id}/ai/generateSection`,
      { blocks, useCustomModel },
      { signal: signal ?? controller.signal },
    );
    return data;
  },

  cancelAllRequest: () => {
    controller.abort(PROGRAMMATIC_ABORT_REASON);
    controller = new AbortController();
  },
};

export const isProgrammaticAIRequestAbort = (error: unknown) => {
  return (
    axios.isCancel(error) && axios.isAxiosError(error) && getAbortReason(error.config?.signal) === PROGRAMMATIC_ABORT_REASON
  );
};
